import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AdminHeader, Badge } from "@/pages/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Wand2, Trash2, BarChart3 } from "lucide-react";

const BENEFITS = [
    { v: "referral_only", label: "Referral tracking only" },
    { v: "free_months", label: "Free Basic months" },
    { v: "percent_discount", label: "Percentage discount" },
    { v: "amount_discount", label: "Dollar discount" },
    { v: "intro_price", label: "Special intro price" },
];

const BLANK = { code: "", campaign_name: "", referral_source: "", benefit_type: "referral_only",
    free_months: 1, discount_percent: 0, discount_amount: 0, max_uses: "", start_date: "",
    expiry_date: "", active: true, conversion_plan: "standard", paypal_plan_id: "" };

export default function Codes() {
    const qc = useQueryClient();
    const [form, setForm] = useState(BLANK);
    const [editing, setEditing] = useState(null);
    const [stats, setStats] = useState(null);
    const { data: codes } = useQuery({ queryKey: ["admin-codes"], queryFn: async () => (await api.get("/admin/codes")).data });

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const gen = () => {
        const base = (form.referral_source || form.campaign_name || "SSC").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8) || "SSC";
        setForm({ ...form, code: base + Math.floor(10 + Math.random() * 89) });
    };
    const save = async () => {
        try {
            const payload = { ...form, max_uses: form.max_uses === "" ? null : Number(form.max_uses),
                free_months: Number(form.free_months) || 0, discount_percent: Number(form.discount_percent) || 0,
                discount_amount: Number(form.discount_amount) || 0 };
            if (editing) await api.put(`/admin/codes/${editing}`, payload);
            else await api.post("/admin/codes", payload);
            toast.success(editing ? "Code updated." : "Code created.");
            setForm(BLANK); setEditing(null);
            qc.invalidateQueries({ queryKey: ["admin-codes"] });
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const edit = (c) => { setEditing(c.id); setForm({ ...BLANK, ...c, max_uses: c.max_uses ?? "" }); window.scrollTo({ top: 0, behavior: "smooth" }); };
    const del = async (id) => { if (!window.confirm("Delete this code?")) return; await api.delete(`/admin/codes/${id}`); qc.invalidateQueries({ queryKey: ["admin-codes"] }); };
    const showStats = async (id) => { try { setStats((await api.get(`/admin/codes/${id}/analytics`)).data); } catch (e) { toast.error("Could not load analytics."); } };

    return (
        <div>
            <AdminHeader title="Access & Partner Codes" subtitle="Create codes for influencers and partners, track sign-ups per code, and see who referred each member." />

            <div className="mb-6 rounded-lg border border-zinc-200 bg-white p-5" data-testid="code-form">
                <h3 className="mb-3 text-sm font-700 text-zinc-800">{editing ? "Edit code" : "Create a new code"}</h3>
                <div className="grid gap-3 sm:grid-cols-3">
                    <div className="sm:col-span-1">
                        <Label>Code</Label>
                        <div className="mt-1 flex gap-2">
                            <Input data-testid="code-input" value={form.code} onChange={set("code")} placeholder="FELICITY30" className="uppercase" />
                            <Button type="button" variant="outline" size="icon" title="Auto-generate" onClick={gen}><Wand2 className="h-4 w-4" /></Button>
                        </div>
                    </div>
                    <div><Label>Associate / referral source</Label><Input data-testid="code-source" value={form.referral_source} onChange={set("referral_source")} placeholder="Felicity G. Morgan" className="mt-1" /></div>
                    <div><Label>Campaign name</Label><Input value={form.campaign_name} onChange={set("campaign_name")} placeholder="Felicity Client Promo" className="mt-1" /></div>
                    <div>
                        <Label>Benefit</Label>
                        <Select value={form.benefit_type} onValueChange={(v) => setForm({ ...form, benefit_type: v })}>
                            <SelectTrigger data-testid="code-benefit" className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>{BENEFITS.map((b) => <SelectItem key={b.v} value={b.v}>{b.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    {form.benefit_type === "free_months" && <div><Label>Free months</Label><Input type="number" min="0" value={form.free_months} onChange={set("free_months")} className="mt-1" /></div>}
                    {form.benefit_type === "percent_discount" && <div><Label>Discount %</Label><Input type="number" min="0" max="100" value={form.discount_percent} onChange={set("discount_percent")} className="mt-1" /></div>}
                    {form.benefit_type === "amount_discount" && <div><Label>Discount $</Label><Input type="number" min="0" value={form.discount_amount} onChange={set("discount_amount")} className="mt-1" /></div>}
                    <div>
                        <Label>Conversion plan</Label>
                        <Select value={form.conversion_plan} onValueChange={(v) => setForm({ ...form, conversion_plan: v })}>
                            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="standard">Basic $20/mo</SelectItem><SelectItem value="unlimited">Unlimited $50/mo</SelectItem></SelectContent>
                        </Select>
                    </div>
                    <div><Label>Max uses (blank = unlimited)</Label><Input type="number" min="1" value={form.max_uses} onChange={set("max_uses")} placeholder="100" className="mt-1" /></div>
                    <div><Label>Start date</Label><Input type="date" value={form.start_date || ""} onChange={set("start_date")} className="mt-1" /></div>
                    <div><Label>Expiry date</Label><Input type="date" value={form.expiry_date || ""} onChange={set("expiry_date")} className="mt-1" /></div>
                    <div><Label>PayPal plan ID (optional override)</Label><Input value={form.paypal_plan_id || ""} onChange={set("paypal_plan_id")} placeholder="P-XXXXXXXX" className="mt-1" /></div>
                </div>
                <div className="mt-4 flex items-center gap-2">
                    <Button data-testid="code-save" onClick={save}><Plus className="mr-1 h-4 w-4" /> {editing ? "Save changes" : "Create code"}</Button>
                    {editing && <Button variant="ghost" onClick={() => { setForm(BLANK); setEditing(null); }}>Cancel</Button>}
                </div>
            </div>

            <div className="rounded-lg border border-zinc-200 bg-white overflow-x-auto">
                <Table>
                    <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Associate</TableHead><TableHead>Benefit</TableHead><TableHead>Uses</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                    <TableBody>
                        {(codes || []).map((c) => (
                            <TableRow key={c.id} data-testid={`code-row-${c.code}`}>
                                <TableCell className="font-mono font-700 text-zinc-800">{c.code}</TableCell>
                                <TableCell>{c.referral_source || "—"}</TableCell>
                                <TableCell className="max-w-[240px] truncate text-sm text-zinc-500">{c.benefit_text}</TableCell>
                                <TableCell>{c.uses || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</TableCell>
                                <TableCell><Badge tone={c.active ? "green" : "zinc"}>{c.active ? "Active" : "Inactive"}</Badge></TableCell>
                                <TableCell className="text-right">
                                    <Button size="sm" variant="ghost" data-testid={`code-stats-${c.code}`} onClick={() => showStats(c.id)}><BarChart3 className="h-4 w-4" /></Button>
                                    <Button size="sm" variant="ghost" onClick={() => edit(c)}>Edit</Button>
                                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => del(c.id)}><Trash2 className="h-4 w-4" /></Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        {(codes || []).length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-zinc-400" data-testid="codes-empty">No codes yet. Create one above.</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={!!stats} onOpenChange={() => setStats(null)}>
                <DialogContent data-testid="code-analytics-dialog">
                    <DialogHeader><DialogTitle className="font-mono">{stats?.code} <span className="ml-2 font-sans text-sm font-400 text-zinc-500">{stats?.referral_source}</span></DialogTitle></DialogHeader>
                    {stats && (
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            {[["Code uses", stats.uses], ["Accounts created", stats.accounts_created], ["Verified members", stats.verified_members],
                              ["Paid conversions", stats.paid_conversions], ["Basic conversions", stats.basic_conversions], ["Unlimited conversions", stats.unlimited_conversions],
                              ["Canceled", stats.canceled], ["Revenue (MRR)", `$${stats.revenue_mrr}`]].map(([l, v]) => (
                                <div key={l} className="rounded-lg bg-zinc-50 p-3"><p className="text-xs text-zinc-500">{l}</p><p className="text-lg font-700 text-zinc-800">{v}</p></div>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
